/**
 * Confirm-transaction store.
 *
 * Imperative API: `requestTransactionConfirmation(payload)` returns a promise
 * that resolves on user confirm and rejects (with `TransactionRejectedError`)
 * on cancel.
 *
 * The matching dialog component subscribes to the store and renders the
 * payload + Confirm/Cancel buttons. Mounted once at app shell (via
 * `WalletProvider`) so any submit pipeline (oracle action, SOL transfer,
 * token transfer) can pop a confirmation step without per-feature plumbing.
 *
 * Why imperative + Zustand: every submit pipeline today is a plain async
 * function (no React tree access). Wiring this through React context would
 * force every pipeline to become a hook. The store + imperative entrypoint
 * keeps the pipelines pure.
 *
 * Ported from detrust-oracle/workers/frontend/src/features/wallet/store/
 * confirm-tx-store.ts as part of Phase 6 slice 4 — replaces the legacy
 * `transaction-guard-store` + auto-decode approach.
 */

import { create } from "zustand";

export interface ConfirmTxDetail {
  /** Left column label, e.g. "Wallet" / "Bet amount". */
  label: string;
  /** Right column display value (already formatted by the caller). */
  value: string;
}

export interface ConfirmTxItem {
  /** Primary heading for the item — e.g. market title. */
  title: string;
  /** Optional secondary line — e.g. "YES • 0.5 SOL → 0.95 SOL". */
  subtitle?: string;
  /**
   * Optional emphasis colour on the subtitle. Mapped by the dialog to a
   * Tailwind text class; defaults to neutral. Lets callers show
   * yes/no/positive/negative without leaking presentation into the api layer.
   */
  tone?: "yes" | "no" | "muted";
}

export interface ConfirmTxPayload {
  /** Action title shown at the top. e.g. "Place Bet". */
  title: string;
  /** Optional one-line description below the title. */
  description?: string;
  /** Detail rows rendered as a label/value table. */
  details: ConfirmTxDetail[];
  /**
   * Optional structured list rendered ABOVE `details`. Use for batch
   * previews where each item (e.g. one bet) needs its own card-like row
   * instead of being flattened into a label/value pair.
   */
  items?: ConfirmTxItem[];
}

export class TransactionRejectedError extends Error {
  constructor(message = "Transaction rejected by user") {
    super(message);
    this.name = "TransactionRejectedError";
  }
}

interface ConfirmTxState {
  open: boolean;
  payload: ConfirmTxPayload | null;
  resolver: ((confirmed: boolean) => void) | null;

  request: (payload: ConfirmTxPayload) => Promise<void>;
  confirm: () => void;
  cancel: () => void;
}

export const useConfirmTxStore = create<ConfirmTxState>((set, get) => ({
  open: false,
  payload: null,
  resolver: null,

  request: (payload) =>
    new Promise<void>((resolve, reject) => {
      // If a previous prompt is still open (caller bug), reject the pending
      // one so we don't silently leak its promise.
      const prev = get().resolver;
      if (prev) {
        prev(false);
      }
      const wrappedResolver = (confirmed: boolean): void => {
        if (confirmed) resolve();
        else reject(new TransactionRejectedError());
      };
      set({ open: true, payload, resolver: wrappedResolver });
    }),

  confirm: () => {
    const { resolver } = get();
    set({ open: false, payload: null, resolver: null });
    resolver?.(true);
  },

  cancel: () => {
    const { resolver } = get();
    set({ open: false, payload: null, resolver: null });
    resolver?.(false);
  },
}));

/**
 * Imperative entry point. Resolves on confirm, rejects with
 * `TransactionRejectedError` on cancel.
 */
export function requestTransactionConfirmation(
  payload: ConfirmTxPayload,
): Promise<void> {
  return useConfirmTxStore.getState().request(payload);
}
